<template>
  <div class="pricing-container" @close="close">
    <h2 class="title">Pricing Plans</h2>
    <p class="subtitle" v-if="isFreeUser">
      Choose the plan that works for you: all self-serve plans include a
      {{ trialDays }}-day free trial.
    </p>
    <p v-else class="subtitle">Choose the plan that works for you.</p>

    <div class="billing-toggle">
      <button
        class="billing-option"
        :class="{ active: isYearly }"
        @click="isYearly = true"
      >
        <span>Yearly Billing</span>
        <span class="discount-label">{{ yearlyDiscountLabel }}</span>
      </button>
      <button
        class="billing-option"
        :class="{ active: !isYearly }"
        @click="isYearly = false"
      >
        Monthly Billing
      </button>
    </div>

    <div class="plans" v-if="tiers.length">
      <div class="plan" v-for="tier in tiers" :key="tier.id">
        <div
          class="card"
          :class="{
            featured: tier.featured,
            active: isCurrentTier(tier)
          }"
        >
          <h5 class="plan-title">{{ tier.label }}</h5>

          <h3 class="plan-price">
            <template v-if="tier.id === 'enterprise'">Custom</template>
            <template v-else>
              €{{ displayMonthlyPrice(tier) }}<small>/month</small>
            </template>
          </h3>

          <p
            class="billing-info"
            v-if="tier.id === 'base' || tier.id === 'pro'"
          >
            Billed {{ isYearly ? "yearly" : "monthly" }}
          </p>
          <p v-else class="billing-info billing-info--placeholder" aria-hidden="true">
            &nbsp;
          </p>

          <button
            v-if="isCurrentTier(tier)"
            class="subscribe-btn btn-secondary"
            disabled
          >
            Active
          </button>
          <button
            v-else
            class="subscribe-btn"
            :class="tier.featured ? 'btn-warning' : 'btn-success'"
            @click="onTierCta(tier)"
          >
            {{ ctaLabel(tier) }}
          </button>

          <ul class="feature-list">
            <li v-for="feature in tier.features" :key="feature">
              ✓ <span class="feature-text">{{ feature }}</span>
            </li>
          </ul>

          <p v-for="note in tier.notes || []" :key="note" class="plan-note">
            {{ note }}
          </p>
        </div>
      </div>
    </div>

    <v-dialog v-model="showEnterpriseModal" max-width="760" width="96vw">
      <v-card class="enterprise-dialog-card" dark>
        <v-card-title class="headline enterprise-dialog-title">
          Enterprise + MDR
        </v-card-title>
        <v-card-text
          v-if="!enterpriseSubmitted"
          class="enterprise-dialog-body"
        >
          <EnterpriseInquiryForm
            v-model="enterpriseForm"
            :deployment-type-options="deploymentTypeOptions"
            :estimated-storage-options="estimatedStorageOptions"
          />
        </v-card-text>

        <v-card-text v-else>
          <p class="mb-2">Thank you. We'll contact you shortly.</p>
          <p class="mb-4">
            If your request is urgent, you can book a call below.
          </p>
          <v-btn
            v-if="enterpriseCalendlyUrl"
            color="primary"
            :href="enterpriseCalendlyUrl"
            target="_blank"
            rel="noopener"
          >
            Book a Call
          </v-btn>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showEnterpriseModal = false">Close</v-btn>
          <v-btn
            v-if="!enterpriseSubmitted"
            class="enterprise-submit-btn"
            color="primary"
            @click="submitEnterprise"
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import EnterpriseInquiryForm from "@/components/EnterpriseInquiryForm.vue";

export default {
  name: "PurchaseSubscription",
  components: {
    EnterpriseInquiryForm
  },
  data() {
    return {
      isYearly: true,
      showEnterpriseModal: false,
      enterpriseSubmitted: false,
      enterpriseCalendlyUrl: "",
      enterpriseForm: {
        name: "",
        work_email: "",
        organization: "",
        role: "",
        country: "",
        deployment_type: [],
        estimated_users: "",
        estimated_storage: "",
        message: ""
      }
    };
  },
  computed: {
    ...mapGetters("billing", ["catalog", "isBillingEnabled"]),
    trialDays() {
      return this.catalog?.trial_days || 14;
    },
    yearlyDiscountLabel() {
      return this.catalog?.yearly_discount_label || "Save 20%";
    },
    tiers() {
      return this.catalog?.tiers || [];
    },
    deploymentTypeOptions() {
      return this.catalog?.enterprise?.fields?.deployment_type || [];
    },
    estimatedStorageOptions() {
      return this.catalog?.enterprise?.fields?.estimated_storage || [];
    },
    currentTier() {
      return (this.$store.state.auth.user?.subscription_tier || "free").toLowerCase();
    },
    isFreeUser() {
      return this.currentTier === "free";
    }
  },
  async mounted() {
    if (!this.catalog) {
      await this.$store.dispatch("billing/fetchBillingCatalog");
    }
  },
  methods: {
    close() {
      this.$emit("close");
    },
    isCurrentTier(tier) {
      return this.currentTier === tier.id;
    },
    displayMonthlyPrice(tier) {
      if (!tier.price) return "0";
      return this.isYearly ? tier.price.yearly_eur : tier.price.monthly_eur;
    },
    ctaLabel(tier) {
      if (tier.id === "free" && !this.isFreeUser) {
        return "Switch to Free";
      }
      if (this.isFreeUser || tier.cta?.action !== "checkout") {
        return tier.cta?.label || "Select";
      }
      if (tier.id === "base") return "Switch to Basic";
      if (tier.id === "pro") return "Switch to Pro";
      return tier.cta?.label || "Select";
    },
    async onTierCta(tier) {
      if (!this.isBillingEnabled) {
        return;
      }
      if (tier.cta.action === "enterprise_inquiry") {
        this.showEnterpriseModal = true;
        this.enterpriseSubmitted = false;
        this.enterpriseCalendlyUrl = this.catalog?.enterprise?.calendly_url || "";
        return;
      }
      if (tier.cta.action !== "checkout") {
        return;
      }

      const priceId = this.isYearly
        ? tier.price?.price_id?.yearly
        : tier.price?.price_id?.monthly;
      if (!priceId) {
        this.$store.commit("raiseSnackbar", {
          text: "Selected plan is not available in this environment.",
          color: "warning",
          timeout: 4000
        });
        return;
      }

      try {
        const response = await Vue.$http.post(
          "api/create-checkout-session/",
          null,
          { price_id: priceId }
        );
        const payload = response?.data || response?.body || response;
        if (payload?.url) {
          window.location.href = payload.url;
        }
      } catch (error) {
        this.$store.commit("raiseSnackbar", {
          text: "Checkout error. Please try again.",
          color: "error",
          timeout: 4000
        });
        console.error(error);
      }
    },
    async submitEnterprise() {
      const required = [
        "name",
        "work_email",
        "organization",
        "role",
        "country",
        "estimated_users",
        "estimated_storage"
      ];
      const missing = required.filter(key => !this.enterpriseForm[key]);
      if (missing.length || !this.enterpriseForm.deployment_type.length) {
        this.$store.commit("raiseSnackbar", {
          text: "Please complete all required fields.",
          color: "warning",
          timeout: 4000
        });
        return;
      }

      try {
        const response = await Vue.$http.post(
          "api/enterprise-inquiry/",
          null,
          this.enterpriseForm
        );
        const payload = response?.data || response?.body || response;
        this.enterpriseSubmitted = true;
        this.enterpriseCalendlyUrl =
          payload?.calendly_url || this.catalog?.enterprise?.calendly_url || "";
      } catch (error) {
        this.$store.commit("raiseSnackbar", {
          text: "Unable to submit request. Please try again.",
          color: "error",
          timeout: 4000
        });
        console.error(error);
      }
    }
  }
};
</script>

<style scoped>
.pricing-container {
  max-width: 1040px;
  margin: 0 auto;
  padding: 24px;
  color: #f5f5f5;
}

.title {
  text-align: center;
  font-weight: 700;
  margin-bottom: 8px;
}

.subtitle {
  text-align: center;
  color: #c7c7c7;
  margin-bottom: 20px;
}

.billing-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.billing-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #4a4a4a;
  border-radius: 999px;
  padding: 8px 14px;
  background: #2a2a2a;
  color: #f5f5f5;
  cursor: pointer;
}

.billing-option.active {
  border-color: #f0bd4a;
  background: #f0bd4a;
  color: #1e1e1e;
}

.discount-label {
  font-size: 11px;
  line-height: 1;
  padding: 3px 8px;
  border-radius: 999px;
  background: #2e7d32;
  color: #f1fff2;
  font-weight: 700;
}

.billing-option.active .discount-label {
  background: #1e1e1e;
  color: #f0bd4a;
}

.plans {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.plan {
  flex: 1 1 230px;
  min-width: 230px;
}

.card {
  border: 1px solid #3a3a3a;
  border-radius: 12px;
  padding: 16px;
  height: 100%;
  background: #1e1e1e;
}

.card.featured {
  border-color: #f6a400;
  box-shadow: 0 4px 12px rgba(246, 164, 0, 0.2);
}

.card.active {
  border-color: #2e7d32;
}

.plan-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.plan-price {
  font-size: 28px;
  font-weight: 700;
}

.billing-info {
  color: #b5b5b5;
  font-size: 13px;
}

.billing-info--placeholder {
  visibility: hidden;
}

.subscribe-btn {
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 10px;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  margin: 12px 0;
}

.btn-success {
  background: #2e7d32;
}

.btn-warning {
  background: #f6a400;
}

.btn-secondary {
  background: #999;
}

.feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 13px;
}

.feature-list li {
  margin-bottom: 6px;
}

.plan-note {
  margin-top: 8px;
  color: #bdbdbd;
  font-size: 12px;
}

.enterprise-dialog-card {
  background: #1e1e1e;
  border: 1px solid #3a3a3a;
}

.enterprise-dialog-title {
  border-bottom: 1px solid #3a3a3a;
  font-weight: 700;
}

.enterprise-dialog-body {
  padding-top: 20px;
}

.enterprise-submit-btn {
  font-weight: 700;
}

::v-deep .enterprise-dialog-card .v-input__slot {
  background: #2a2a2a !important;
  border-color: #3f3f3f !important;
}

::v-deep .enterprise-dialog-card .v-label {
  color: #d5d5d5 !important;
}

::v-deep .enterprise-dialog-card .v-chip {
  background: #3a3a3a !important;
}

@media (max-width: 900px) {
  .plan {
    min-width: 260px;
  }
}

@media (max-width: 620px) {
  .plan {
    min-width: 100%;
  }
}
</style>
